import type { Word, VerbsData, Batch, ModelSettings, ModelSettingsResponse, LLMRequestInfo, TokenUsage, LearningLanguage } from '../types';

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(cb: () => void): void {
  onUnauthorized = cb;
}

async function apiFetch(url: string, opts?: RequestInit): Promise<Response | null> {
  const resp = await fetch(url, opts);
  if (resp.status === 401) {
    if (onUnauthorized) onUnauthorized();
    return null;
  }
  return resp;
}

export async function fetchWords(): Promise<Word[]> {
  const resp = await apiFetch('/api/words');
  if (!resp) return [];
  return resp.json();
}

export async function fetchVerbs(learningLanguage?: LearningLanguage): Promise<VerbsData> {
  const search = learningLanguage ? `?learningLanguage=${encodeURIComponent(learningLanguage)}` : '';
  const resp = await apiFetch(`/api/verbs${search}`);
  if (!resp) return {};
  return resp.json();
}

export async function generateSentences(
  payload: object
): Promise<{ sentences: string[]; usedWords: string[]; raw?: string; llmRequest?: LLMRequestInfo; tokenUsage?: TokenUsage }> {
  const resp = await apiFetch('/api/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp) return { sentences: [], usedWords: [] };
  if (!resp.ok) {
    const text = await resp.text();
    let message = `Generation failed (${resp.status})`;
    try { message = JSON.parse(text).error || message; } catch { /* use default */ }
    throw new Error(message);
  }
  return resp.json();
}

export async function translateSentences(
  sentences: string[],
  learningLanguage?: LearningLanguage,
): Promise<{ translations: string[]; llmRequest?: LLMRequestInfo; tokenUsage?: TokenUsage }> {
  const resp = await apiFetch('/api/translate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sentences, ...(learningLanguage ? { learningLanguage } : {}) }),
  });
  if (!resp) return { translations: [] };
  if (!resp.ok) {
    const text = await resp.text();
    let message = `Translation failed (${resp.status})`;
    try { message = JSON.parse(text).error || message; } catch { /* use default */ }
    throw new Error(message);
  }
  return resp.json();
}

export async function login(password: string): Promise<boolean> {
  const resp = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return resp.ok;
}

export async function logout(): Promise<void> {
  await fetch('/api/logout', { method: 'POST' });
}

export async function fetchSettings(): Promise<Record<string, unknown> | null> {
  const resp = await apiFetch('/api/settings');
  if (!resp || !resp.ok) return null;
  const data = await resp.json();
  return data.settings || null;
}

export async function saveSettings(settings: object): Promise<void> {
  await apiFetch('/api/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings }),
  });
}

export async function fetchHistory(): Promise<Batch[] | null> {
  const resp = await apiFetch('/api/history');
  if (!resp || !resp.ok) return null;
  const data = await resp.json();
  return data.history || null;
}

export async function addHistoryBatch(batch: Batch): Promise<void> {
  await apiFetch('/api/history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ batch }),
  });
}

export async function updateHistoryBatch(timestamp: number, updates: Partial<Batch>): Promise<void> {
  await apiFetch(`/api/history/${timestamp}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteHistoryBatch(timestamp: number): Promise<void> {
  await apiFetch(`/api/history/${timestamp}`, {
    method: 'DELETE',
  });
}

export async function fetchModelSettings(): Promise<ModelSettingsResponse | null> {
  const resp = await apiFetch('/api/model-settings');
  if (!resp || !resp.ok) return null;
  return resp.json();
}

export async function saveModelSettings(modelSettings: ModelSettings): Promise<void> {
  await apiFetch('/api/model-settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ modelSettings }),
  });
}

export async function chatWithLLM(
  message: string,
  systemPrompt?: string,
): Promise<{ text: string; llmRequest?: LLMRequestInfo; tokenUsage?: TokenUsage }> {
  const resp = await apiFetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, systemPrompt }),
  });
  if (!resp) return { text: '' };
  if (!resp.ok) {
    const text = await resp.text();
    let errorMessage = `Chat failed (${resp.status})`;
    try { errorMessage = JSON.parse(text).error || errorMessage; } catch { /* use default */ }
    throw new Error(errorMessage);
  }
  return resp.json();
}

export async function fetchChatSystemPrompt(): Promise<string | null> {
  const resp = await apiFetch('/api/chat-system-prompt');
  if (!resp || !resp.ok) return null;
  const data = await resp.json();
  return data.systemPrompt || null;
}

export async function saveChatSystemPrompt(systemPrompt: string): Promise<void> {
  await apiFetch('/api/chat-system-prompt', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ systemPrompt }),
  });
}
